/**
 * Cookie-consent state helper (GDPR).
 *
 * Backs a three-state decision stored in localStorage:
 *
 *   - `null`      — user hasn't answered yet. Third-party analytics
 *                   (PostHog, GA4) stay dormant; the banner renders.
 *   - `accepted`  — analytics may initialise.
 *   - `declined`  — analytics must remain dormant permanently.
 *
 * Sentry error tracking and Crisp live chat are exempt from this gate
 * (legitimate-interest + user-initiated respectively) and init
 * unconditionally from main.tsx.
 *
 * `setConsent` fires a `window` CustomEvent named `altai-consent` so
 * the analytics loaders (which register a one-time listener when
 * they boot before consent) can bootstrap themselves the moment the
 * user clicks Accept. No reload needed.
 */
const STORAGE_KEY = 'altai_consent';

export type ConsentValue = 'accepted' | 'declined';
export type ConsentState = ConsentValue | null;

export const CONSENT_EVENT = 'altai-consent';

export interface ConsentEventDetail {
  value: ConsentValue;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readConsent(): ConsentState {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'accepted' || raw === 'declined') return raw;
    return null;
  } catch {
    // localStorage blocked (private mode, disabled cookies): treat
    // as "not answered" so the banner keeps prompting and analytics
    // stay dormant.
    return null;
  }
}

export function setConsent(value: ConsentValue): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // If we can't persist, still fire the event so the current
    // session picks up the decision. Subsequent reloads will
    // re-prompt — acceptable failure mode.
  }
  const event = new CustomEvent<ConsentEventDetail>(CONSENT_EVENT, {
    detail: { value },
  });
  window.dispatchEvent(event);
}

export function consentGiven(): boolean {
  return readConsent() === 'accepted';
}
